/**
 * One run, latest or past — the right-hand detail pane, top to bottom: what each of its three phases
 * measured (the `PhaseStage` stack), what went nowhere (`RunShortfall`), and only then which run this
 * is (`RunIdentityCard`).
 *
 * THAT ORDER IS THE DESIGN'S, AND IT CHANGED. The identity card used to come FIRST; all eight flow
 * artboards now order the `Run detail` frame `SyncStatus` then `Run identity` (`323:134`, `334:438`,
 * `334:602`, `334:766`, `334:1109`, `334:1294`, `334:1458`, `544:718` — measured, all eight agree).
 * The numbers are what the reader opened the pane for; the run's own name, timestamp and connection
 * settings are what they check afterwards. Do not re-read an older comment as saying otherwise.
 *
 * This file COMPOSES and does nothing else. It derives no phase logic (`deriveRunPhases` owns that),
 * formats no number (`RunDenominated` owns that), and adds no `-1` sentinel guard of its own — a third
 * guard beside `RunDenominated`'s and `deriveRunPhases`'s would raise the question of which is
 * authoritative.
 *
 * ── WHAT THIS REPLACED ──────────────────────────────────────────────────────
 * A `running ? <LiveRunProgress> : <Callout>` fork over a `TwoSidedDelta` card. Both halves of the
 * fork are gone, and so is the fork: `deriveRunPhases` already encodes the live/settled difference, so
 * there is nothing left to branch on here. `LiveRunProgress` is deleted outright — its bar read 100%
 * while a run was still running and 0% for the whole extract half. The status `Callout` was the error
 * string's old home; the error now sits in `RunIdentityCard`, in the danger token, beside the run it
 * belongs to.
 *
 * A bar is back, and it is NOT that one. `DeliveryProgress` is the last child of the phase card, per
 * Figma `500:1295`: it draws a track on the narrow set of runs where `successful.total / changed.total`
 * is a groundable fraction (a plan over one batch, a run past one reporting flush) and returns `null`
 * on every other run, which is most of them. Read its header before assuming this file re-litigated
 * the deletion — the three properties that made `LiveRunProgress` wrong (unconditional, continuously
 * filling, animated between readings) are each an explicit gate there.
 *
 * ── `Sync mode: Mirror`: THE OBJECTION WAS ANSWERED, NOT OVERRULED ──────────
 * This comment used to end "gone from the footer, and NOT re-homed here … if it should be on the page
 * it belongs in the header with the other sync-level facts." Read the complaint precisely: the footer
 * was wrong because a SYNC-scoped setting sat beside `Type:`, a RUN-scoped one, "with nothing
 * distinguishing the two scopes". The fault was the missing scope, not the location.
 *
 * Figma `334:514` (band `539:1005`) supplies the missing scope and brings the setting back on that
 * basis: a tinted, top-bordered band at the foot of `RunIdentityCard` whose first line IS the scope —
 * `CONNECTION` — carrying Sync mode and Log retention together. That is a
 * deliberate re-homing that answers the original objection, not one that ignores it: nothing on the
 * pane can now be read as claiming these two settings describe the open run. The header remains a
 * defensible alternative home; it is not the one the design chose, and this note is here so the next
 * reader knows the question was decided rather than forgotten. `ConnectionSettingsBand` holds the
 * reasoning and the retention rule that came with it.
 *
 * ── THE ACCESSIBILITY TIE ───────────────────────────────────────────────────
 * The `<h2>` this file used to render — `Run from ${runStartedLabel(run)}` — has MOVED into
 * `RunIdentityCard`, which builds it from the same `runStartedLabel(run)` call. That tie is what lets
 * a screen-reader user match this pane back to the row they opened: `RunsPanel`'s row activator
 * announces `Open run from ${runStartedLabel(run)}`, and `aria-selected` on that row is spec-valid but
 * not reliably announced (a plain `role="table"` is not conventionally a selection widget, and
 * promoting it to `role="grid"` was ruled out). There is exactly ONE heading on this pane, and it is
 * the card's — do not add a second here.
 *
 * ── THE STACKING CONTAINER ──────────────────────────────────────────────────
 * The card, the border and the radius live on the CONTAINER; the three rows are transparent and
 * stacked with no gap, which is what lets the spine run edge to edge across them (see `PhaseStage`'s
 * header, and `stories/PhaseStage.stories.tsx`'s `ContinuousSpine`, the only place it can be
 * eyeballed).
 *
 * `items-center` and vertical-only padding, rather than the stories' `p-4`: `PhaseStage` declares
 * `w-[22rem]` (352px) and `RunsPanel`'s pane is `w-[23rem]` (368px), of which the card's own 1px
 * borders take 2 — so there are 14px to spend and `p-4` would want 32, overflowing the pane and
 * scrolling it sideways. Centring spends the 14 as a symmetric ~7px gutter instead of stranding it at
 * one edge, and does it WITHOUT hoisting a width onto this container: the 1rem shortfall is a known,
 * accepted design question (see `PhaseStage`'s note at its JSX root) and closing it is not this file's
 * call.
 *
 * `RunShortfall` sits BELOW the phase card, not inside it — every row in the card reports what one
 * phase measured, and that number is precisely what no phase measured. It also TRAVELS with that card:
 * it describes the numbers immediately above it, so the two are wrapped together and the identity card
 * follows the pair. Split apart, the shortfall line would sit under the run's timestamp and connection
 * settings, describing nothing next to it.
 *
 * ── THE 12px, WHICH USED TO BE 16 ───────────────────────────────────────────
 * The wrapper carries `mb-3` (12px) and it is the ONLY gap between the two cards. That is Figma's
 * `itemSpacing`, measured on all eight `Run detail` frames listed above; the old `mb-4` (16px) lived on
 * `RunIdentityCard` when the identity card came first and had no design behind it. 12 is also what
 * `RunShortfall` already brings as its own `pt-3`, so the pane's internal rhythm is now one number
 * rather than two.
 *
 * The margin is on the WRAPPER, not on the phase card: `RunShortfall`'s `pt-3` must stay the only gap
 * between the stack and the shortfall line (a `mb-3` on the card itself would add a second 12px there
 * whenever the line renders). And nothing carries a trailing bottom margin — `RunIdentityCard` is last
 * and ends the pane.
 */
import { MessageSquareText } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import { Card } from '@/_shared/components/ui/card';

import { deriveRunPhases } from '../lib/run-phases';
import { runHasFailures } from '../lib/run-view';
import type { IdentifierMapping, RawSyncRun } from '../types';
import { DeliveryProgress } from './DeliveryProgress';
import { PhaseStack, PhaseStage } from './PhaseStage';
import { RunIdentityCard } from './RunIdentityCard';
import { RunShortfall } from './RunShortfall';

interface RunOverviewProps {
  run: RawSyncRun;
  /** The far-side audience by name where known, else its id, else the account — see the ladder. */
  /** Registry key for the destination's brand mark, e.g. `FB_CUSTOM_AUDIENCE`. Send's row only. */
  destinationDefinitionName?: string | undefined;
  /**
   * `RawSqlAudience.sourceDefinitionName` — the warehouse that did the extracting, for Extract's mark.
   * DATA-DRIVEN: `PhaseMark` resolves it case-insensitively through `warehouseTypeMeta` and falls back
   * to the generic warehouse glyph, so an absent or unrecognised value degrades rather than guesses.
   */
  warehouseName?: string | undefined;
  /**
   * `sync.config.destination.syncMode` — CONNECTION-scoped, and passed through to the identity card's
   * band rather than mixed into any per-run line. See this file's header for why it is back at all.
   */
  syncMode?: string | undefined;
  /**
   * This connection's own retention, already resolved by `retentionDays(sync)`. REQUIRED and `null`
   * means retention is OFF — never back-fill the platform default on the way through here.
   */
  retentionDays: number | null;
  /**
   * `sync.config.destination.identifierMappings` — CONNECTION-scoped, like `syncMode`, and passed
   * through to the identity card's band. See `ConnectionSettingsBand` for what the three states mean.
   */
  identifierMappings?: IdentifierMapping[] | undefined;
  /** `sync.config.destination.fieldMappings` — LinkedIn's or Gainsight PX's second table; see the band's doc. */
  fieldMappings?: IdentifierMapping[] | undefined;
  /** The second table's name for this destination; see the band's doc. */
  fieldMappingsLabel?: string | undefined;
  /** Opens the sync's edit sheet, from the connection band's `Edit`. */
  onEditConnection?: (() => void) | undefined;
  /** Why `Edit` on the connection band is unavailable. Threaded through — see the band's own prop. */
  editConnectionDisabledReason?: string | undefined;
  /**
   * Opens the activation failure chat. Set by `RunsPanel` only where the chat exists for this page;
   * the `Ask why` button renders only when it is set AND this run has something to explain
   * (`runHasFailures`, so a run that stopped before counting records still has an entry). Hidden rather than
   * disabled: the unset cases are "the surface does not exist", not "unavailable right now".
   */
  onAskWhy?: (() => void) | undefined;
}

export function RunOverview({
  run,
  destinationDefinitionName,
  warehouseName,
  syncMode,
  retentionDays,
  identifierMappings,
  fieldMappings,
  fieldMappingsLabel,
  onEditConnection,
  editConnectionDisabledReason,
  onAskWhy,
}: RunOverviewProps): React.ReactElement {
  const phases = deriveRunPhases(run);

  return (
    <div>
      {/* The phase card and the line that describes its numbers, as ONE block — see this file's header
          for why they cannot be separated, and why the 12px gap below them lives here rather than on
          either child. */}
      <div className="mb-3">
        {/* The container carries the card; the rows are transparent and `gap-0` — see this file's
          header for why, and for why the padding is vertical-only with the rows centred. */}
        {/* `Card` supplies the border, radius and surface. `data-slot` is spread AFTER the primitive's
          own inside `Card`, so this one wins and `run-phase-stack` survives — the tests query it and
          the spine's stacking contract is described by it. `gap-0` and the asymmetric vertical
          padding override the primitive's `gap-6`/`py-6` through `twMerge`: the rows must TOUCH, or
          the hairline running between them is nicked once per row.

          14 top / 12 bottom, not `py-4`'s 16/16 — the spec's own figures (Figma `500:1295`). The
          asymmetry is not a slip: the first row's stat block sits at the top of its 46px mark band
          while the last row's breakdown line stops short of its band's bottom edge, so equal padding
          reads as more air underneath than above. */}
        <Card data-slot="run-phase-stack" className="items-center gap-0 pt-[0.875rem] pb-3">
          {/* `PhaseStack`: equal rows, so the spine spans the block — Extract's mark at the top, Plan's
              centred, Send's at the bottom (see `PhaseStage`'s header). */}
          <PhaseStack>
            {phases.map((phase, index) => (
              <PhaseStage
                key={phase.phase}
                phase={phase}
                isFirst={index === 0}
                isLast={index === phases.length - 1}
                warehouseName={warehouseName}
                destinationDefinitionName={destinationDefinitionName}
              />
            ))}
          </PhaseStack>

          {/* INSIDE the card, under the phase rows, exactly as Figma `500:1295` draws it — the `rule`
            at `500:1069` and the `Delivery` frame at `500:1070` are siblings of the `status` frame,
            not of the card. `DeliveryProgress` owns its own rule and returns `null` in every state
            that must not have one, which is most of them; this file stays a composer and re-derives
            none of that gate. See that component's header for what the gate is and why. */}
          <DeliveryProgress run={run} />
        </Card>

        <RunShortfall run={run} />
        {onAskWhy && runHasFailures(run) ? (
          <div className="pt-3">
            <Button
              variant="secondary"
              size="md"
              Icon={<MessageSquareText aria-hidden />}
              onClick={onAskWhy}
              data-chat-entry=""
            >
              Ask why
            </Button>
          </div>
        ) : null}
      </div>

      {/* LAST, and with no bottom margin of its own — it ends the pane. */}
      <RunIdentityCard
        run={run}
        syncMode={syncMode}
        retentionDays={retentionDays}
        identifierMappings={identifierMappings}
        fieldMappings={fieldMappings}
        fieldMappingsLabel={fieldMappingsLabel}
        onEditConnection={onEditConnection}
        editConnectionDisabledReason={editConnectionDisabledReason}
      />
    </div>
  );
}
